import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Address, countryDefaultValue } from "@models/address/address";
import { EntityType } from "@models/common/entity-type.enum";
import { Company } from "@models/companies/company";
import { EntityData } from "@models/companies/entity-data";
import { Document } from "@models/documents/document";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { IndividualData } from "@models/individuals/individual-data";
import {
  Relationship,
  ShareholderRelationshipDetails,
  TrusteeRelationshipDetails
} from "@models/relationships/relationship";
import { RelationshipType, RelationshipTypeLabels } from "@models/relationships/relationship-type.enum";
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from "@models/shares/security-registry-record";
import { Trust } from "@models/trusts/trust";
import {
  IndividualCorporateRelationship,
  individualCorporateTrusteeOptions,
} from "@models/trusts/trust-change-relationship";
import { jurisdictionOptions, JurisdictionType, TrustType } from "@models/trusts/trusts-enums";
import { SelectOption } from "@models/ui-models/select-options";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { CheckboxGroupComponent } from "@shared/controls/checkbox-group/checkbox-group.component";
import { FlagsAutocompleteComponent } from "@shared/controls/flags-autocomplete/flags-autocomplete.component";
import { InputNumberComponent } from "@shared/controls/input-number/input-number.component";
import { InputComponent } from "@shared/controls/input/input.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { clearFormArray } from "@shared/utils/clear-form-array";
import { Guid } from "@shared/utils/guid.helper";
import { parseFullName } from "@shared/utils/parse-fullname";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { NumbersValidators } from "@shared/validators/numbers.validators";
import {
  IntegrationsEntitySearchControlComponent
} from "../../../controls/integrations-entity-search-control/integrations-entity-search-control.component";
import { TrustDiscretionaryEstablishment } from "../../../models/trusts/trust-discretionary-establishment.model";
import {
  AddressFormGroup,
  AddressFormGroupComponent
} from "../../../reusable-form-groups/address-form-group/address-form-group.component";
import {
  CorporateRelationshipFormGroup,
  CorporateRelationshipFormGroupComponent
} from "../../../reusable-form-groups/corporate-relationship-form-group/corporate-relationship-form-group.component";
import {
  IndividualRelationshipFormGroup,
  IndividualRelationshipFormGroupComponent,
} from "../../../reusable-form-groups/relationship-form-group/individual-relationship-form-group.component";
import {
  AuthorisedSignatoriesStepComponent
} from "../../../reusable-form-parts/authorised-signatories-step/authorised-signatories-step.component";
import { StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";
import {
  BaseTrustEstablishmentFormComponent
} from "../base-trust-establishment-form/base-trust-establishment-form.component";
import { getTrustEstablishmentChangeType } from "../constants/trust-establishment-texts";
import {
  DiscretionaryTrustEstablishmentFormSteps,
  DiscretionaryTrustEstablishmentFormStepsEnum
} from "./constants/discretionary-trust-establishment-form-steps";

export interface IIndividualCorporateRelationshipFormControls {
  individualId: FormControl<string | null>,
  trusteeType: FormControl<IndividualCorporateRelationship | null>;
  individualTrustee: IndividualRelationshipFormGroup;
  corporateTrustee: CorporateRelationshipFormGroup;
  types: FormControl<RelationshipType[]>;
  relationshipId: FormControl<string | null>;
}

export type IIndividualCorporateRelationshipFormGroup = FormGroup<IIndividualCorporateRelationshipFormControls>;

interface IBeneficiaryFormGroupControls {
  individualId: FormControl<string | null>;
  name: FormControl<string | null>;
  address: AddressFormGroup;
}

type IBeneficiaryFormGroup = FormGroup<IBeneficiaryFormGroupControls>

@Component({
  selector: 'app-discretionary-trust-establishment-form',
  standalone: true,
  imports: [
    StepperFormComponent,
    ReactiveFormsModule,
    SelectComponent,
    StepperFormDescriptionComponent,
    IntegrationsEntitySearchControlComponent,
    FlagsAutocompleteComponent,
    AddressFormGroupComponent,
    InputNumberComponent,
    InputComponent,
    DividerComponent,
    IndividualRelationshipFormGroupComponent,
    CorporateRelationshipFormGroupComponent,
    CheckboxGroupComponent,
    ButtonComponent,
    AuthorisedSignatoriesStepComponent,
  ],
  templateUrl: 'trust-discretionary-establishment.component.html',
  styleUrls: [
    'trust-discretionary-establishment.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss'
  ]
})
export class TrustDiscretionaryEstablishmentComponent extends BaseTrustEstablishmentFormComponent<DiscretionaryTrustEstablishmentFormStepsEnum, TrustDiscretionaryEstablishment> implements OnInit, AfterViewInit {
  readonly settledSumTooltipMessage = 'The settled sum needs to be a nominal value, for example $10.';
  readonly individualCorporateTrusteeOptions: SelectOption[] = individualCorporateTrusteeOptions;
  readonly EntityType = EntityType;
  readonly jurisdictionOptions = jurisdictionOptions;
  readonly IndividualCorporateRelationship = IndividualCorporateRelationship;
  readonly maxPrincipalAppointorsToAdd = 4;

  override StepsEnum = DiscretionaryTrustEstablishmentFormStepsEnum;
  override steps = DiscretionaryTrustEstablishmentFormSteps;

  relationshipTypesOptions: SelectOption[] = [
    { label: RelationshipTypeLabels[RelationshipType.Trustee], value: RelationshipType.Trustee, disabled: true },
    { label: RelationshipTypeLabels[RelationshipType.Beneficiary], value: RelationshipType.Beneficiary },
  ];
  isSimpleBeneficiariesFillingMode = true;
  authorisedSecurityRegistryRecords: SecurityRegistryRecord[] = [];

  override stepperForm = new FormGroup({
    [this.StepsEnum.FormDescription]: new FormGroup({}),
    [this.StepsEnum.TrustDetailsAndAddress]: new FormGroup({
      trustName: new FormControl<string | null>(null, [Validators.required, CustomFormValidators.maxLength(50)]),
      settledSum: new FormControl<number | null>(null, [Validators.required, NumbersValidators.isInteger]),
      jurisdiction: new FormControl<JurisdictionType | null>(null, [Validators.required]),
      meetingAddress: AddressFormGroupComponent.defineForm()
    }),
    [this.StepsEnum.SettlorAndPrincipalAppointor]: new FormGroup({
      settlor: this.createIndividualCorporateRelationshipFormGroup({ type: RelationshipType.Settlor }),
      appointors: new FormArray<IndividualRelationshipFormGroup>([IndividualRelationshipFormGroupComponent.defineForm()])
    }),
    [this.StepsEnum.Trustees]: new FormGroup({
      trustees: new FormArray<IIndividualCorporateRelationshipFormGroup>([this.createTrusteeFormGroup({ type: RelationshipType.Trustee }, true)])
    }),
    [this.StepsEnum.Beneficiaries]: new FormGroup({
      beneficiaries: new FormArray([this.createBeneficiaryFormGroup()])
    }),
    [this.StepsEnum.AuthorisedSignatories]: AuthorisedSignatoriesStepComponent.defineForm(),
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.isSimpleBeneficiariesFillingMode = !this.isEdit;
    this.redirectAfterSubmit = true;
    this.updateFormSteps();
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.setupChange();
  }

  override afterSubmit(changes: EntityChangeData[]) {
    const trustDiscretionaryEstablishmentChange = changes.find((change) => change.$type === TrustDiscretionaryEstablishment.$type) as TrustDiscretionaryEstablishment;
    this.setupChange(trustDiscretionaryEstablishmentChange);
  }

  override setupChange(change: TrustDiscretionaryEstablishment = this.formModel) {
    if (!this.isEdit && change === this.formModel) return;

    clearFormArray(this.appointorsFormArray);
    clearFormArray(this.trusteesFormArray);

    this.trustDetailsForm.patchValue(change);

    this.settlorAndAppointorForm.controls.settlor.patchValue({
      trusteeType: change.settlor.individualDataOverride ? IndividualCorporateRelationship.Individual : IndividualCorporateRelationship.Corporate,
      individualTrustee: {
        name: change.settlor?.individualDataOverride?.fullName,
        address: change.settlor?.individualDataOverride?.address
      },
      corporateTrustee: {
        name: change.settlor?.entityDataOverride?.name,
        acn: change.settlor.entityDataOverride?.entityNumber ?? null,
        address: change.settlor?.entityDataOverride?.registeredAddress,
        authorisedSignatories: (change.settlor.details as TrusteeRelationshipDetails | null)?.authorisedSignatories ?? []
      }
    });

    change.appointors.forEach((relationship) => {
      const appointorFormGroup = IndividualRelationshipFormGroupComponent.defineForm();

      appointorFormGroup.patchValue({
        name: relationship?.individualDataOverride?.fullName,
        address: relationship?.individualDataOverride?.address
      });

      this.appointorsFormArray.push(appointorFormGroup);
    });

    this.setupTrusteeChange(change);
    this.setupBeneficiriesChange(change);
    this.updateFormSteps();
  }

  override setCurrentStep(newStepIndex: number) {
    super.setCurrentStep(newStepIndex);
    this.updateFormSteps();

    if (this.currentStep == this.StepsEnum.Beneficiaries && this.isSimpleBeneficiariesFillingMode) {
      const trusteesFormsWithBeneficiaryChecked = this.trusteesFormArray.controls
        .filter((trusteeFormGroup) => trusteeFormGroup.controls.trusteeType.value == IndividualCorporateRelationship.Individual && trusteeFormGroup.controls.types.value.includes(RelationshipType.Beneficiary));

      if (trusteesFormsWithBeneficiaryChecked.length) {
        clearFormArray(this.beneficiariesFormArray);
        trusteesFormsWithBeneficiaryChecked
          .forEach((trusteeFormGroup) => {
            if (!trusteeFormGroup.controls.individualId.value) {
              trusteeFormGroup.controls.individualId.setValue(Guid.generate());
            }

            const beneficiaryFormGroup = this.createBeneficiaryFormGroup({
              individualId: trusteeFormGroup.controls.individualId.value,
              individualDataOverride: new IndividualData({
                fullName: trusteeFormGroup.controls.individualTrustee.controls.name.value ?? '',
                address: new Address(trusteeFormGroup.controls.individualTrustee.controls.address.value as Address)
              }),
            });

            setControlDisabled(beneficiaryFormGroup);
            this.beneficiariesFormArray.push(beneficiaryFormGroup);
          });
      } else {
        this.setIsSimpleBeneficiariesFillingMode(false);
      }
    }

    if (this.currentStep == this.StepsEnum.AuthorisedSignatories) {
      this.fillAuthorisedSignatories();
    }
  }

  override updateFormSteps(): void {
    this.steps = DiscretionaryTrustEstablishmentFormSteps.filter((step) => {

      if (step.step == this.StepsEnum.AuthorisedSignatories) {
        return this.isAuthorisedSignatoriesStepVisible();
      }

      return true;
    });
  }

  setupTrusteeChange(change: TrustDiscretionaryEstablishment): void {
    const trusteeBeneficiaryTypes = [RelationshipType.Trustee, RelationshipType.Beneficiary];

    const relationshipsByTypes = [...change.trustees, ...change.beneficiaries]
      .filter((relationship) => trusteeBeneficiaryTypes.includes(relationship.type))
      .reduce((relationships, relationship) => {
        const id = relationship.individualId ?? relationship.entityId ?? relationship.relationshipId;

        if (relationships[id]) {
          relationships[id].push(relationship);
        } else {
          relationships[id] = [relationship];
        }

        return relationships;
      }, {} as Record<string, Relationship[]>);

    const trusteesFormGroups = Object.values(relationshipsByTypes)
      .filter((relationships) => relationships.some((relationship) => relationship.type == RelationshipType.Trustee))
      .reduce((forms, relationships, index) => {
        const trusteeFormGroup = this.createTrusteeFormGroup(relationships[0], index == 0);
        const types = relationships.map((relationship) => relationship.type);

        trusteeFormGroup.patchValue({ types });
        forms.push(trusteeFormGroup);

        if (relationships[0].entityDataOverride && relationships[0].details?.$type == TrusteeRelationshipDetails.$type) {
          const details = relationships[0].details as TrusteeRelationshipDetails;
          trusteeFormGroup.controls.corporateTrustee.controls.authorisedSignatories.patchValue(details.authorisedSignatories ?? []);
        }

        return forms;
      }, [] as IIndividualCorporateRelationshipFormGroup[]);

    trusteesFormGroups.forEach((trusteeFormGroup) => this.trusteesFormArray.push(trusteeFormGroup));
  }

  setupBeneficiriesChange(change: TrustDiscretionaryEstablishment): void {
    clearFormArray(this.beneficiariesFormArray);
    const beneficiariesFormGroups = change.beneficiaries
      .filter((relationship) => relationship.type == RelationshipType.Beneficiary)
      .map((relationship) => this.createBeneficiaryFormGroup(relationship));

    beneficiariesFormGroups.forEach((beneficiaryFormGroup) => this.beneficiariesFormArray.push(beneficiaryFormGroup));
  }

  onTrustSelected(trust: Trust): void {
    this.trustDetailsForm.patchValue({
      ...trust,
      trustName: trust.name
    });
  }

  createBeneficiaryFormGroup(relationship: Partial<Relationship> = {}): IBeneficiaryFormGroup {
    const beneficiaryFormGroup = new FormGroup({
      individualId: new FormControl<string | null>(null),
      name: new FormControl<string | null>(null),
      address: AddressFormGroupComponent.defineForm(),
    });

    beneficiaryFormGroup.patchValue({
      individualId: relationship?.individualId ?? null,
      name: relationship.individualDataOverride?.fullName ?? null,
      address: relationship.individualDataOverride?.address ?? new Address({ country: countryDefaultValue }),
    });

    return beneficiaryFormGroup;
  }

  addAppointor(): void {
    if (this.appointorsFormArray.controls.length >= this.maxPrincipalAppointorsToAdd) return;
    const appointorFormGroup = IndividualRelationshipFormGroupComponent.defineForm();
    this.appointorsFormArray.push(appointorFormGroup);
  }

  addBeneficiary(): void {
    this.setIsSimpleBeneficiariesFillingMode(false);
    const beneficiaryForm = this.createBeneficiaryFormGroup();
    this.beneficiariesFormArray.push(beneficiaryForm);
  }

  removeAppointor(index: number): void {
    if (this.appointorsFormArray.length <= 1) return;
    this.appointorsFormArray.removeAt(index);
  }

  removeBeneficiary(index: number): void {
    this.beneficiariesFormArray.removeAt(index);
  }

  override buildDocument(): Document {
    const settlor = new Relationship({
      type: RelationshipType.Settlor,
      relationshipId: Guid.generate(),
    });

    if (this.settlorAndAppointorForm.controls.settlor.controls.trusteeType.value == IndividualCorporateRelationship.Individual) {
      settlor.individualDataOverride = new IndividualData({
        address: new Address(this.settlorAndAppointorForm.controls.settlor.controls.individualTrustee.controls.address.value as Address),
        fullName: this.settlorAndAppointorForm.controls.settlor.controls.individualTrustee.controls.name.value ?? '',
        ...parseFullName(this.settlorAndAppointorForm.controls.settlor.controls.individualTrustee.controls.name.value ?? '') as Partial<IndividualData>
      });
    } else {
      const registeredAddress: Address = new Address(this.settlorAndAppointorForm.controls.settlor.controls.corporateTrustee.controls.address.value as Address);
      const authorisedSignatories = this.settlorAndAppointorForm.controls.settlor.controls.corporateTrustee.controls.authorisedSignatories.value ?? [];

      settlor.details = new TrusteeRelationshipDetails({ authorisedSignatories });
      settlor.entityDataOverride = new EntityData({
        name: this.settlorAndAppointorForm.controls.settlor.controls.corporateTrustee.controls.name.value ?? '',
        entityNumber: this.settlorAndAppointorForm.controls.settlor.controls.corporateTrustee.controls.acn.value ?? undefined,
        registeredAddress,
      });
    }

    const appointors = this.appointorsFormArray.controls.map((appointorFormGroup) => {
      return new Relationship({
        type: RelationshipType.Appointor,
        relationshipId: Guid.generate(),
        individualDataOverride: new IndividualData({
          address: new Address(appointorFormGroup.controls.address.value as Address),
          fullName: appointorFormGroup.controls.name.value ?? '',
          ...parseFullName(appointorFormGroup.controls.name.value ?? '') as Partial<IndividualData>
        })
      });
    });

    const trustees = this.trusteesFormArray.controls.map((trusteeFormGroup) => {
      if (trusteeFormGroup.controls.trusteeType.value == IndividualCorporateRelationship.Corporate) {
        const authorisedSignatories = trusteeFormGroup.controls.corporateTrustee.controls.authorisedSignatories.value ?? [];

        return new Relationship({
          type: RelationshipType.Trustee,
          relationshipId: Guid.generate(),
          entityId: Guid.generate(),
          entityDataOverride: new Company({
            acn: trusteeFormGroup.controls.corporateTrustee.controls.acn.value ?? '',
            entityNumber: trusteeFormGroup.controls.corporateTrustee.controls.acn.value ?? '',
            name: trusteeFormGroup.controls.corporateTrustee.controls.name.value ?? '',
            registeredAddress: new Address(trusteeFormGroup.controls.corporateTrustee.controls.address.value as Address),
          }),
          details: new TrusteeRelationshipDetails({ authorisedSignatories })
        });
      }

      return new Relationship({
        type: RelationshipType.Trustee,
        relationshipId: Guid.generate(),
        individualId: trusteeFormGroup.controls.individualId.value,
        individualDataOverride: new IndividualData({
          address: new Address(trusteeFormGroup.controls.individualTrustee.controls.address.value as Address),
          fullName: trusteeFormGroup.controls.individualTrustee.controls.name.value ?? '',
          ...parseFullName(trusteeFormGroup.controls.individualTrustee.controls.name.value ?? '') as Partial<IndividualData>
        }),
      });
    });

    const beneficiaries = this.beneficiariesFormArray.controls.map((beneficiaryFormGroup) => {
      return new Relationship({
        type: RelationshipType.Beneficiary,
        relationshipId: Guid.generate(),
        individualId: beneficiaryFormGroup.controls.individualId.value ?? Guid.generate(),
        individualDataOverride: new IndividualData({
          address: new Address(beneficiaryFormGroup.controls.address.value as Address),
          fullName: beneficiaryFormGroup.controls.name.value ?? '',
          ...parseFullName(beneficiaryFormGroup.controls.name.value ?? '') as Partial<IndividualData>
        }),
      });
    });

    const change = new TrustDiscretionaryEstablishment({
      trustName: this.trustDetailsForm.controls.trustName.value ?? '',
      trustType: this.trustType ?? TrustType.Discretionary,
      subType: this.trustSubtype,
      appointors,
      settlor,
      trustees,
      beneficiaries,
      meetingAddress: new Address(this.trustDetailsForm.controls.meetingAddress.value as Address),
      jurisdiction: this.trustDetailsForm.controls.jurisdiction.value!,
      settledSum: this.trustDetailsForm.controls.settledSum.value ?? 0
    });

    return new Document({
      changes: [change],
      entityId: this.entityId,
      type: getTrustEstablishmentChangeType(this.trustSubtype),
      documentId: this.documentId,
    });
  }

  setIsSimpleBeneficiariesFillingMode(isSimpleBeneficiariesFillingMode: boolean): void {
    this.isSimpleBeneficiariesFillingMode = isSimpleBeneficiariesFillingMode;

    if (isSimpleBeneficiariesFillingMode) return;

    // Enable beneficiaries type and set it's value to itself
    // the rest of job performs valueChanges listeners of form
    this.beneficiariesFormArray.controls.forEach((beneficiaryFormGroup) => beneficiaryFormGroup.enable());
  }

  isAuthorisedSignatoriesStepVisible(): boolean {
    const isSettlorCorporate = this.settlorAndAppointorForm.controls.settlor.controls.trusteeType.value == IndividualCorporateRelationship.Corporate;
    const isTrusteeCorporate = this.trusteesFormArray.length == 1 && this.trusteesFormArray.at(0).controls.trusteeType.value == IndividualCorporateRelationship.Corporate;

    return isSettlorCorporate || isTrusteeCorporate;
  }

  fillAuthorisedSignatories(): void {
    const corporateRelationshipFormGroups: IIndividualCorporateRelationshipFormGroup[] = [
      this.settlorAndAppointorForm.controls.settlor,
      this.trusteesFormArray.at(0),
    ]
      .filter((relationshipFormGroup) => {
        return relationshipFormGroup.controls.trusteeType.value != null && relationshipFormGroup.controls.trusteeType.value == IndividualCorporateRelationship.Corporate;
      });

    const authorisedSecurityRegistryRecords = corporateRelationshipFormGroups
      .map((relationshipFormGroup) => {
        let holders: (IndividualHolderModel | CorporateHolderModel)[] = [];
        const formValue = relationshipFormGroup.controls.corporateTrustee.value;
        const relationshipId = relationshipFormGroup.controls.relationshipId.value ?? Guid.generate();

        if (!relationshipFormGroup.controls.relationshipId.value) {
          relationshipFormGroup.controls.relationshipId.setValue(relationshipId);
        }

        const entityData = new EntityData({
          name: formValue.name ?? '',
          entityNumber: formValue.acn ?? '',
          registeredAddress: new Address(formValue.address as Partial<Address>),
        });

        const details = new ShareholderRelationshipDetails({
          authorisedSignatories: formValue.authorisedSignatories ?? []
        });

        const holder = new CorporateHolderModel({
          entityData,
          relationshipId,
          details
        });

        holders = [holder];

        return new SecurityRegistryRecord({ holders });
      });

    const isSettlorAndTrusteeOneCompany = this.settlorAndAppointorForm.controls.settlor.controls.relationshipId.value == this.trusteesFormArray.at(0).controls.relationshipId.value
      || this.settlorAndAppointorForm.controls.settlor.controls.corporateTrustee.controls.name.value == this.trusteesFormArray.at(0).controls.corporateTrustee.controls.name.value;

    if (isSettlorAndTrusteeOneCompany) {
      this.authorisedSecurityRegistryRecords = authorisedSecurityRegistryRecords.map((holder) => {
        const relationshipTypes = [RelationshipType.Settlor, RelationshipType.Trustee]
          .map((type) => RelationshipTypeLabels[type])
          .join(', ');
        const corporateHolderModel = (holder.holders[0] as CorporateHolderModel);

        corporateHolderModel.entityData.name = `${ corporateHolderModel.entityData.name } (${ relationshipTypes })`;

        return holder;
      });
    } else {
      this.authorisedSecurityRegistryRecords = authorisedSecurityRegistryRecords.map((holder, index) => {
        const relationshipType = corporateRelationshipFormGroups[index].controls.types.value[0] as RelationshipType;
        const relationshipTypeString = RelationshipTypeLabels[relationshipType];
        const corporateHolderModel = (holder.holders[0] as CorporateHolderModel);

        corporateHolderModel.entityData.name = `${ corporateHolderModel.entityData.name } (${ relationshipTypeString })`;

        return holder;
      });
    }
  }

  onCorporateHolderChangeChange(holder: CorporateHolderModel): void {
    const isSettlorTypeCorporate = this.settlorAndAppointorForm.controls.settlor.controls.trusteeType.value == IndividualCorporateRelationship.Corporate;
    const isSettlorRelationshipIdEqual = this.settlorAndAppointorForm.controls.settlor.controls.relationshipId.value == holder.relationshipId;
    const isSettlorCorporateNameEqual = this.settlorAndAppointorForm.controls.settlor.controls.corporateTrustee.controls.name.enabled && this.settlorAndAppointorForm.controls.settlor.controls.corporateTrustee.controls.name.value == holder.name;

    if (isSettlorTypeCorporate && (isSettlorRelationshipIdEqual || isSettlorCorporateNameEqual)) {
      this.settlorAndAppointorForm.controls.settlor.controls.corporateTrustee.controls.authorisedSignatories.setValue(holder.details.authorisedSignatories ?? null);
    }

    const isTrusteeFormGroupsQuantityEqualOne = this.trusteesFormArray.length == 1;
    const isTrusteeTypeCorporate = this.trusteesFormArray.at(0).controls.trusteeType.value == IndividualCorporateRelationship.Corporate;
    const isTrusteeRelationshipIdEqual = this.trusteesFormArray.at(0).controls.relationshipId.value == holder.relationshipId;
    const isTrusteeCorporateNameEqual = this.trusteesFormArray.at(0).controls.corporateTrustee.controls.name.enabled && this.trusteesFormArray.at(0).controls.corporateTrustee.controls.name.value == holder.name;

    if (isTrusteeFormGroupsQuantityEqualOne && isTrusteeTypeCorporate && (isTrusteeRelationshipIdEqual || isTrusteeCorporateNameEqual)) {
      this.trusteesFormArray.at(0).controls.corporateTrustee.controls.authorisedSignatories.setValue(holder.details.authorisedSignatories ?? null);
    }
  }

  get trustDetailsForm() {
    return this.stepperForm.controls[this.StepsEnum.TrustDetailsAndAddress];
  }

  get settlorAndAppointorForm() {
    return this.stepperForm.controls[this.StepsEnum.SettlorAndPrincipalAppointor];
  }

  get appointorsFormArray() {
    return this.stepperForm.controls[this.StepsEnum.SettlorAndPrincipalAppointor].controls.appointors;
  }

  override get trusteesFormArray() {
    return this.stepperForm.controls[this.StepsEnum.Trustees].controls.trustees;
  }

  get beneficiariesFormArray() {
    return this.stepperForm.controls[this.StepsEnum.Beneficiaries].controls.beneficiaries;
  }

  get authorisedSignatoriesForm() {
    return this.stepperForm.controls[this.StepsEnum.AuthorisedSignatories];
  }
}
